import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { useMediaQuery } from 'react-responsive';

import { NextIcon, PreviousIcon } from 'honeydew/icons/Carousels';
import useStickyShopNavigation, { STICKY_SHOP_NAVIGATION_STYLES } from 'hooks/useStickyShopNavigation';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { scrollToL2AisleElement } from 'utils/aislesUtils';
import { trackAisleNavigation } from 'utils/analyticsUtils';

export function AisleSubcategoryNavigationWrapper({ children }) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const stickyShopNavigation = useStickyShopNavigation();
    const navRef = useRef();

    const [stuck, setStuck] = useState(false);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const checkArrows = () => {
        if (!navRef?.current) return;
        if (!mobile && navRef.current.scrollWidth > navRef.current.clientWidth + 20) {
            setShowLeftArrow(navRef.current.scrollLeft !== 0);
            setShowRightArrow(navRef.current.scrollLeft + navRef.current.clientWidth < navRef.current.scrollWidth);
        }
    };

    useEffect(() => {
        checkArrows();
    }, []);

    const scrollLeft = () => {
        if (typeof navRef?.current?.scrollLeft === 'number') {
            navRef.current.scrollLeft -= 400;
        }
    };

    const scrollRight = () => {
        if (typeof navRef?.current?.scrollLeft === 'number') {
            navRef.current.scrollLeft += 400;
        }
    };

    const { entry, ref } = useInView({
        threshold: [1],
        rootMargin: '-81px 0px 0px 0px',
    });

    useEffect(() => {
        setStuck(entry?.intersectionRatio < 1);
    }, [entry]);

    return (
        <div
            ref={ref}
            className={clsx('bg-malt if:bg-oat md:mx-0 md:bg-white md:px-0 md:if:bg-white', {
                'bg-white py-10': !mobile,
                [`sticky top-[90px] z-10 ${STICKY_SHOP_NAVIGATION_STYLES.duration}`]:
                    stickyShopNavigation.hideSiteNav && mobile && !window.isNativeApp,
                [`sticky top-[226px] z-10 ${STICKY_SHOP_NAVIGATION_STYLES.duration}`]:
                    !stickyShopNavigation.hideSiteNav && mobile && !window.isNativeApp,
                'sticky top-[80px] z-10 !ml-[-30px] !w-[calc(100%+60px)] pr-[30px]': !mobile && !window.isNativeApp,
                'shadow-base': stuck && !mobile && !window.isNativeApp,
                'sticky top-[146px] z-10': window.isNativeApp,
            })}
        >
            {showLeftArrow && (
                <button className="absolute left-10 top-[24px]" type="button" onClick={scrollLeft}>
                    <PreviousIcon width={30} />
                </button>
            )}
            <nav
                className={clsx('flex overflow-auto py-[8px]', {
                    'scroll-mx-[20px] scroll-px-[20px] px-10': mobile,
                    'scroll-mx-[30px] scroll-px-[30px] pl-30 pr-25': !mobile,
                })}
                ref={navRef}
                onScroll={checkArrows}
            >
                {children}
            </nav>
            {showRightArrow && (
                <button className="absolute right-10 top-[22px]" type="button" onClick={scrollRight}>
                    <NextIcon width={30} />
                </button>
            )}
        </div>
    );
}

AisleSubcategoryNavigationWrapper.propTypes = {
    children: PropTypes.node.isRequired,
};

function AisleSubcategoryNavigation({ categories, currentCategoryId, setCurrentCategoryId }) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    useEffect(() => {
        const element = document.querySelector(`#nav-${currentCategoryId}`);
        element?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });
    }, [currentCategoryId]);

    return (
        <AisleSubcategoryNavigationWrapper>
            <button
                id="nav-aisle-all"
                type="button"
                className={clsx('text-body-xs mr-5 whitespace-nowrap rounded-md p-10 font-grotesk-bold', {
                    'bg-brand-secondary': currentCategoryId === 'aisle-all',
                    'bg-off-white': currentCategoryId !== 'aisle-all' && !mobile,
                    'bg-white': currentCategoryId !== 'aisle-all' && mobile,
                })}
                onClick={() => {
                    window.scrollTo({ top: 0 });
                    setCurrentCategoryId('aisle-all');
                }}
            >
                All
            </button>

            {categories?.map(({ id, name }) => (
                <span id={`nav-aisle-${id}`} className="pl-10" key={id}>
                    <button
                        type="button"
                        className={clsx('text-body-xs mr-5 whitespace-nowrap rounded-md p-10 font-grotesk-bold', {
                            'bg-brand-secondary': currentCategoryId === `aisle-${id}`,
                            'bg-off-white': currentCategoryId !== `aisle-${id}` && !mobile,
                            'bg-white': currentCategoryId !== `aisle-${id}` && mobile,
                        })}
                        onClick={() => {
                            scrollToL2AisleElement({ mobile, selector: `#aisle-${id}` });
                            setCurrentCategoryId(`aisle-${id}`);
                            trackAisleNavigation({
                                aisleName: name,
                                level: 2,
                                mobile,
                                uiElement: 'L2',
                            });
                        }}
                    >
                        {name}
                    </button>
                </span>
            ))}
        </AisleSubcategoryNavigationWrapper>
    );
}

AisleSubcategoryNavigation.propTypes = {
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    currentCategoryId: PropTypes.string.isRequired,
    setCurrentCategoryId: PropTypes.func.isRequired,
};

export default AisleSubcategoryNavigation;
